'use client'

import { memo, useCallback, useEffect, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'

type NavLinkItem = { label: string; href: string; kind: 'link' }
type NavScrollItem = { label: string; id: string; kind: 'scroll' }
type NavItem = NavLinkItem | NavScrollItem

/** baby: Same items + behavior as classic /ninxl nav: craft styling only. */
const NAV_ITEMS: NavItem[] = [
  { label: 'NIN', href: '/nin', kind: 'link' },
  { label: 'NINK', href: '/nink', kind: 'link' },
  { label: 'Ningenie', href: '/home', kind: 'link' },
  { label: 'Capabilities', id: 'features', kind: 'scroll' },
  { label: 'Apps', id: 'works-in', kind: 'scroll' },
  { label: 'Pricing', id: 'pricing', kind: 'scroll' },
  { label: 'Contact', id: 'contact', kind: 'scroll' },
  { label: 'Enterprise', href: '/ninxl/enterprise', kind: 'link' },
  { label: 'Govts', href: '/ninxl/govts', kind: 'link' },
  { label: 'Banks', href: '/ninxl/banks', kind: 'link' },
]

export const CraftNav = memo(function CraftNav({
  onScrollTo,
}: {
  onScrollTo?: (id: string) => void
}) {
  const pathname = usePathname()
  const [scrolled, setScrolled] = useState(false)
  // baby: Craft is the default /ninxl landing.
  const isLanding = pathname === '/ninxl'

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const scrollTo = useCallback(
    (id: string) => {
      if (onScrollTo) {
        onScrollTo(id)
        return
      }
      const target =
        document.getElementById(id) ??
        document.querySelector<HTMLElement>(`[data-section="${id}"]`)
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    [onScrollTo],
  )

  return (
    <nav
      className={`craft-nav${scrolled ? ' is-scrolled' : ''}`}
      aria-label="NINX navigation"
    >
      <div className="craft-nav__inner craft-nav__inner--wide">
        <Link href={isLanding ? pathname : '/ninxl'} className="craft-nav__brand">
          <Image
            src="/ninx_logo_light.svg"
            alt="NINX"
            width={28}
            height={28}
            className="craft-nav__mark"
            priority
          />
          <span className="craft-nav__wordmark">NINX</span>
        </Link>

        <div className="craft-nav__links craft-nav__links--dense">
          {NAV_ITEMS.map((item) => {
            if (item.kind === 'link') {
              const isActive = pathname === item.href
              return (
                <Link
                  key={item.label}
                  href={item.href}
                  className={isActive ? 'is-active' : undefined}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              )
            }

            if (isLanding) {
              return (
                <button key={item.label} type="button" onClick={() => scrollTo(item.id)}>
                  {item.label}
                </button>
              )
            }

            return (
              <Link key={item.label} href={`/ninxl#${item.id}`}>
                {item.label}
              </Link>
            )
          })}
        </div>

        {isLanding ? (
          <button type="button" className="craft-nav__cta" onClick={() => scrollTo('download')}>
            Download
          </button>
        ) : (
          <Link href="/ninxl#download" className="craft-nav__cta">
            Download
          </Link>
        )}
      </div>
    </nav>
  )
})

CraftNav.displayName = 'CraftNav'
